import React from "react";
import { Dealer } from "@/types/dealer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Phone, Star, Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { DealersCitySEO } from "@/components/dealers/DealersCitySEO";
import { Button } from "@/components/ui/button";

interface ServerDealersCityContentProps {
  dealers: Dealer[];
  formattedCity: string;
  citySlug?: string;
  cityStats?: any;
}

/**
 * Server-side safe version of DealersCityContent
 * This component renders a simplified version of the dealers by city list during server-side rendering
 * and includes SEO optimization for server-side rendering
 */
export function ServerDealersCityContent({ 
  dealers, 
  formattedCity, 
  citySlug,
  cityStats 
}: ServerDealersCityContentProps) {
  const { createDealerCanonicalPath, createCitySlug } = useDealerUtils();
  
  // Generate citySlug if not provided
  const finalCitySlug = citySlug || createCitySlug(formattedCity);
  
  // If no dealers, render a loading state
  if (!dealers || dealers.length === 0) {
    return (
      <>
        {/* Include SEO component for server-side rendering */}
        <DealersCitySEO 
          formattedCity={formattedCity || 'Caricamento...'} 
          citySlug={finalCitySlug} 
          dealers={[]} 
          cityStats={cityStats}
        />
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Compro Oro a {formattedCity || 'Caricamento...'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  console.log(`[ServerDealersCityContent] Rendering dealers for ${formattedCity}:`, dealers.length);
  
  return (
    <>
      {/* Include SEO component for server-side rendering */}
      <DealersCitySEO 
        formattedCity={formattedCity} 
        citySlug={finalCitySlug} 
        dealers={dealers} 
        cityStats={cityStats}
      />
      
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold mb-4">Compro Oro a {formattedCity}</h2>
          <p className="text-muted-foreground mb-6">
            Trova i migliori compro oro a {formattedCity}. Confronta prezzi, recensioni e servizi.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dealers.map((dealer) => (
              <Link
                key={dealer.id}
                to={createDealerCanonicalPath(dealer.id, dealer.city)}
                className="block"
              >
                <Card className="h-full hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{dealer.name}</h3>
                    
                    {/* Address */}
                    <div className="flex items-start mb-2">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-1" />
                      <div>
                        <p className="text-sm">{dealer.address}</p>
                        {dealer.city && (
                          <p className="text-sm">{dealer.city}{dealer.postal_code ? `, ${dealer.postal_code}` : ''}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Phone */}
                    {dealer.phone && (
                      <div className="flex items-center mb-2">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                        <p className="text-sm">{dealer.phone}</p>
                      </div>
                    )}
                    
                    {/* Rating */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 mr-1 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-medium">
                          {typeof dealer.rating === 'number' ? dealer.rating.toFixed(1) : '4.0'}
                        </span>
                        <span className="text-sm text-muted-foreground ml-1">/ 5</span>
                      </div>
                      
                      {/* Report Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Navigate to dealer detail page with report modal
                          window.location.href = createDealerCanonicalPath(dealer.id, dealer.city) + '?report=true';
                        }}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive"
                        title="Segnala questo commerciante"
                      >
                        <Flag className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="mt-8">
            <Link
              to="/compro-oro"
              className="text-primary hover:underline"
            >
              ← Torna a tutte le città
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
